import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  extractAllAccountMedia,
  supportsDirectoryPicker,
  type ExtractDestination,
  type ExtractProgress,
} from '../../services/accountExtract';
import type { ProfileMediaItem, ProfileUser } from '../../services/mediaContracts';

type StepId = 'confirm' | 'destination' | 'running' | 'done';

interface ExtractAllModalProps {
  open: boolean;
  profile: ProfileUser;
  seedItems: ProfileMediaItem[];
  seedCursor: string | null;
  onClose: () => void;
  onItemsDiscovered?: (items: ProfileMediaItem[], nextCursor: string | null) => void;
  onComplete?: (message: string) => void;
}

const formatCount = (n?: number): string => {
  if (n == null) return 'unknown';
  return n.toLocaleString();
};

const phaseLabel = (p: ExtractProgress | null): string => {
  if (!p) return 'Starting…';
  switch (p.phase) {
    case 'discovering':
      return 'Scanning media timeline…';
    case 'downloading':
      return 'Saving files…';
    case 'packing':
      return 'Packing ZIP…';
    case 'done':
      return 'Complete';
    case 'cancelled':
      return 'Cancelled';
    case 'error':
      return 'Something went wrong';
    default:
      return 'Working…';
  }
};

const ExtractAllModal: React.FC<ExtractAllModalProps> = ({
  open,
  profile,
  seedItems,
  seedCursor,
  onClose,
  onItemsDiscovered,
  onComplete,
}) => {
  const canPickFolder = useMemo(() => supportsDirectoryPicker(), []);
  const [step, setStep] = useState<StepId>('confirm');
  const [destination, setDestination] = useState<ExtractDestination>(
    canPickFolder ? 'folder' : 'zip'
  );
  const [progress, setProgress] = useState<ExtractProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const runningRef = useRef(false);

  useEffect(() => {
    if (!open) {
      abortRef.current?.abort();
      abortRef.current = null;
      runningRef.current = false;
      setStep('confirm');
      setProgress(null);
      setError(null);
      setResultMessage(null);
      setDestination(canPickFolder ? 'folder' : 'zip');
    }
  }, [open, canPickFolder]);

  const steps = ['Confirm', 'Destination', 'Extract', 'Done'];
  const activeStep =
    step === 'confirm' ? 0 : step === 'destination' ? 1 : step === 'running' ? 2 : 3;

  const estimated = profile.media_count;
  const largeAccount = (estimated ?? 0) > 500;

  const handleClose = () => {
    if (runningRef.current) {
      abortRef.current?.abort();
    }
    onClose();
  };

  const startExtract = async () => {
    setStep('running');
    setError(null);
    setResultMessage(null);
    setProgress(null);
    runningRef.current = true;

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const result = await extractAllAccountMedia({
        handle: profile.screen_name,
        destination,
        seedItems,
        seedCursor,
        estimatedTotal: estimated,
        signal: controller.signal,
        onProgress: setProgress,
        onItemsDiscovered,
      });

      const msg =
        result.failed > 0
          ? `Saved ${result.saved} of ${result.discovered} files (${result.failed} failed)`
          : `Saved ${result.saved} files` +
            (result.folderName
              ? ` to “${result.folderName}”`
              : result.zipNames?.length
                ? ` as ${result.zipNames.length} ZIP${result.zipNames.length > 1 ? 's' : ''}`
                : '');

      setResultMessage(msg);
      setStep('done');
      onComplete?.(msg);
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') {
        setProgress((prev) =>
          prev
            ? { ...prev, phase: 'cancelled', message: 'Extraction cancelled' }
            : prev
        );
        setResultMessage('Extraction cancelled. Files already saved were kept.');
        setStep('done');
      } else {
        setError(err instanceof Error ? err.message : 'Extraction failed');
        setStep('destination');
      }
    } finally {
      runningRef.current = false;
      abortRef.current = null;
    }
  };

  const progressValue = (() => {
    if (!progress) return 0;
    if (progress.estimatedTotal && progress.estimatedTotal > 0) {
      // Blend discovery + save progress against known media_count
      const discoveredRatio = Math.min(1, progress.discovered / progress.estimatedTotal);
      const savedRatio =
        progress.discovered > 0 ? progress.saved / progress.discovered : 0;
      return Math.min(99, Math.round((discoveredRatio * 0.4 + savedRatio * 0.6) * 100));
    }
    if (progress.phase === 'done') return 100;
    if (progress.discovered === 0) return 5;
    return Math.min(
      95,
      Math.round(
        (progress.saved / Math.max(progress.discovered, 1)) * 80 +
          Math.min(progress.pagesFetched, 10) * 2
      )
    );
  })();

  return (
    <Dialog
      open={open}
      onClose={step === 'running' ? undefined : handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: '#16181C',
          border: '1px solid #2F3336',
          borderRadius: 2,
          m: { xs: 1.5, sm: 4 },
          width: { xs: 'calc(100% - 24px)', sm: '100%' },
          maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 64px)' },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#E7E9EA' }}>
          Extract all media
        </Typography>
        <Typography variant="body2" color="text.secondary">
          @{profile.screen_name}
          {estimated != null ? ` · ~${formatCount(estimated)} media` : ''}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            mb: 3,
            '& .MuiStepLabel-label': { color: '#71767B', fontSize: '0.75rem' },
            '& .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed': {
              color: '#E7E9EA',
            },
            '& .MuiStepIcon-root': { color: '#2F3336' },
            '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': {
              color: '#1D9BF0',
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {step === 'confirm' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body1" sx={{ color: '#E7E9EA', lineHeight: 1.5 }}>
              This will walk @{profile.screen_name}&apos;s public media timeline, load every
              page automatically, and save photos and videos at original quality.
            </Typography>
            <Alert
              severity="info"
              sx={{
                backgroundColor: 'rgba(29, 155, 240, 0.08)',
                color: '#E7E9EA',
                border: '1px solid rgba(29, 155, 240, 0.25)',
                '& .MuiAlert-icon': { color: '#1D9BF0' },
              }}
            >
              Public accounts only. How far back we can go depends on the FXTwitter media
              timeline — it may not reach every historical post on very large accounts.
            </Alert>
            {largeAccount && (
              <Alert severity="warning">
                This account lists ~{formatCount(estimated)} media items. Extraction can take
                a while — keep this tab open, and prefer folder save in Chrome/Edge.
              </Alert>
            )}
            {seedItems.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                Already loaded in gallery: {seedItems.length.toLocaleString()} items
                {seedCursor ? ' · more pages still available' : ' · end of loaded timeline'}.
              </Typography>
            )}
          </Box>
        )}

        {step === 'destination' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body1" sx={{ color: '#E7E9EA' }}>
              Choose where files should go.
            </Typography>
            <RadioGroup
              value={destination}
              onChange={(e) => setDestination(e.target.value as ExtractDestination)}
            >
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: destination === 'folder' ? '#1D9BF0' : '#2F3336',
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  mb: 1.5,
                  backgroundColor:
                    destination === 'folder' ? 'rgba(29,155,240,0.06)' : 'transparent',
                }}
              >
                <FormControlLabel
                  value="folder"
                  disabled={!canPickFolder}
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.5 }}>
                      <FolderOpenIcon sx={{ color: '#1D9BF0', mt: 0.25 }} />
                      <Box>
                        <Typography sx={{ fontWeight: 600, color: '#E7E9EA' }}>
                          Save to a folder
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {canPickFolder
                            ? 'Pick a destination folder. Files write as pages load — best for large accounts.'
                            : 'Not available in this browser. Use Chrome or Edge for folder save.'}
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </Box>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: destination === 'zip' ? '#1D9BF0' : '#2F3336',
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  backgroundColor:
                    destination === 'zip' ? 'rgba(29,155,240,0.06)' : 'transparent',
                }}
              >
                <FormControlLabel
                  value="zip"
                  control={<Radio />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.5 }}>
                      <FolderZipIcon sx={{ color: '#1D9BF0', mt: 0.25 }} />
                      <Box>
                        <Typography sx={{ fontWeight: 600, color: '#E7E9EA' }}>
                          Download as ZIP
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Packs media into ZIP files (split automatically for large accounts).
                          Uses your browser&apos;s default download location.
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </Box>
            </RadioGroup>
          </Box>
        )}

        {step === 'running' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={22} />
              <Typography sx={{ color: '#E7E9EA', fontWeight: 600 }}>
                {phaseLabel(progress)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressValue}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(255,255,255,0.08)',
                '& .MuiLinearProgress-bar': { borderRadius: 4 },
              }}
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1.5,
              }}
            >
              <Stat label="Found" value={progress?.discovered ?? 0} />
              <Stat label="Saved" value={progress?.saved ?? 0} />
              <Stat label="Failed" value={progress?.failed ?? 0} />
              <Stat label="Pages" value={progress?.pagesFetched ?? 0} />
            </Box>
            {progress?.label && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {progress.label}
              </Typography>
            )}
            {progress?.folderName && (
              <Typography variant="body2" color="text.secondary">
                Saving to folder: {progress.folderName}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              Keep this tab open. You can cancel anytime — files already written are kept.
            </Typography>
          </Box>
        )}

        {step === 'done' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleOutlineIcon sx={{ color: '#00BA7C' }} />
              <Typography sx={{ color: '#E7E9EA', fontWeight: 600 }}>
                {progress?.phase === 'cancelled' ? 'Cancelled' : 'Extraction finished'}
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ color: '#E7E9EA' }}>
              {resultMessage}
            </Typography>
            {progress && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 1.5,
                  width: '100%',
                }}
              >
                <Stat label="Found" value={progress.discovered} />
                <Stat label="Saved" value={progress.saved} />
                <Stat label="Failed" value={progress.failed} />
                <Stat label="Pages" value={progress.pagesFetched} />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          pb: {
            xs: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
            sm: 2.5,
          },
          gap: 1,
          flexWrap: 'wrap',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          '& .MuiButton-root': {
            minHeight: 44,
            width: { xs: '100%', sm: 'auto' },
          },
        }}
      >
        {step === 'confirm' && (
          <>
            <Button onClick={handleClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => setStep('destination')}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Continue
            </Button>
          </>
        )}
        {step === 'destination' && (
          <>
            <Button
              onClick={() => setStep('confirm')}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={<CloudDownloadIcon />}
              onClick={startExtract}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {destination === 'folder' ? 'Pick folder & start' : 'Start ZIP extract'}
            </Button>
          </>
        )}
        {step === 'running' && (
          <Button
            color="inherit"
            onClick={() => abortRef.current?.abort()}
            sx={{ textTransform: 'none', color: '#F4212E' }}
          >
            Cancel extraction
          </Button>
        )}
        {step === 'done' && (
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const Stat: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <Box
    sx={{
      px: 1.5,
      py: 1,
      borderRadius: 1.5,
      border: '1px solid #2F3336',
      backgroundColor: '#0F1419',
    }}
  >
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography sx={{ color: '#E7E9EA', fontWeight: 700, fontSize: '1.125rem' }}>
      {value.toLocaleString()}
    </Typography>
  </Box>
);

export default ExtractAllModal;
